import {is, Map as IMap} from 'immutable';
import {memoize, weakMemo} from '@packages/utilities/memo';
import {ExpressionElement} from '@packages/systems/wfdl-ast';
import {
  Component,
  ContextType,
  EnvironmentContextType,
  Expressions,
  Plugins,
} from '@packages/systems/wfdl';
import {emptyArray} from '@packages/utilities/fp/utils';
import {
  isUserComponent,
  getDirectUserComponentDependencies,
  elementContainsNestedSelection,
  isOrContainsElement,
} from '@packages/utilities/platform';
import {
  isPagination,
  isPaginationNext,
  isPaginationPrevious,
  isPaginationCount,
} from '@packages/utilities/elementTypeUtils';
import {
  MutableQualifiedMap,
  getName,
  namesEqual,
  getPluginName,
} from '@packages/systems/wfdl-Qualified';
import {Env} from './Env';
import {
  Environment,
  WebflowEnvironments,
} from '@packages/systems/core-constants';
import {SharedContextType} from '@packages/systems/core/records/SharedContext';
import {ExpansionProps, EvalElementProps} from './types';
import {getIn} from '@packages/systems/core/utils/ImmUtils';
import {getInPojo} from '@packages/utilities/data-utils';
import {BrandedType, applyBrand} from '@packages/utilities/type-safety';
import {getSlotDefaultComponentTypes} from '@packages/systems/component-slots';
const selectedNativeIdPathKey = 'selectedNativeIdPath';
const focusedSymbolKey = 'focusedSymbolNativeIdPath';
const focusedComponentTypeKey = 'focusedComponentType';
const affectedNodeIdsKey = 'affectedNodeIds';
const contentEditorPluginKey = 'contentEditor';

const isPluginContextRecord = (
  pluginContext: ContextType
): pluginContext is Exclude<ContextType, IMap<string, unknown>> => {
  return !IMap.isMap(pluginContext);
};

const getContentEditorSubtreeId = (
  pluginsContext: Env['pluginsContext']
): string | undefined => {
  const contentEditorState = pluginsContext.get(contentEditorPluginKey);

  if (contentEditorState && isPluginContextRecord(contentEditorState)) {
    return contentEditorState.subtree?.id;
  }

  return undefined;
};

type RENDER_REQUIRED = BrandedType<'required', true>;
type RENDER_NOT_REQUIRED = BrandedType<'not-required', false>;

const RENDER = {
  REQUIRED: applyBrand<RENDER_REQUIRED>(true),
  NOT_REQUIRED: applyBrand<RENDER_NOT_REQUIRED>(false),
  UNDECIDED: undefined,
};

type RENDER_INSTRUCTION = (typeof RENDER)[keyof typeof RENDER];

type ElementOptimizer = (
  prev: ExpansionProps,
  next: ExpansionProps
) => RENDER_INSTRUCTION;

type ComponentInstanceOptimizer = (
  p: Env,
  n: Env,
  e: ExpressionElement,
  rootElement: null | ExpressionElement
) => RENDER_INSTRUCTION;

const sharedContextDeopt = (
  prev: {sharedContext: SharedContextType},
  next: {sharedContext: SharedContextType}
) => {
  if (prev.sharedContext !== next.sharedContext) {
    return RENDER.REQUIRED;
  }
};

const evalEnvChangedDeopt = (prevEnv: Env, nextEnv: Env) => {
  if (prevEnv.evalEnv !== nextEnv.evalEnv) {
    return RENDER.REQUIRED;
  }
};

const layoutChangedDeopt = (
  prev: {pluginsContext: EnvironmentContextType},
  next: {pluginsContext: EnvironmentContextType}
) => {
  if (
    !is(prev.pluginsContext.get('Layout'), next.pluginsContext.get('Layout'))
  ) {
    return RENDER.REQUIRED;
  }
};

const tabsChangedDeopt = (
  prev: {pluginsContext: EnvironmentContextType},
  next: {pluginsContext: EnvironmentContextType}
) => {
  if (!is(prev.pluginsContext.get('Tabs'), next.pluginsContext.get('Tabs'))) {
    return RENDER.REQUIRED;
  }
};

const sharedAffectedNodeChangedDeopt = (
  prevEnvironmentContext: EnvironmentContextType,
  nextEnvironmentContext: EnvironmentContextType,
  element: ExpressionElement
) => {
  const prevAffectedNodes = prevEnvironmentContext.get(affectedNodeIdsKey);
  const nextAffectedNodes = nextEnvironmentContext.get(affectedNodeIdsKey);
  if (
    (prevAffectedNodes && prevAffectedNodes.includes(element.id)) ||
    (nextAffectedNodes && nextAffectedNodes.includes(element.id))
  ) {
    return RENDER.REQUIRED;
  }
};
const affectedNodeChangedDeopt = (
  prevEnv: Env,
  nextEnv: Env,
  element: ExpressionElement
) => {
  return sharedAffectedNodeChangedDeopt(
    prevEnv.environmentContext,
    nextEnv.environmentContext,
    element
  );
};

const sharedSelectNodeOptimization = (
  prevEnvironmentContext: EnvironmentContextType,
  nextEnvironmentContext: EnvironmentContextType,
  element: ExpressionElement
) => {
  const prevPath = prevEnvironmentContext.get(selectedNativeIdPathKey);
  const nextPath = nextEnvironmentContext.get(selectedNativeIdPathKey);
  const focusedSymbolNativeIdPath =
    nextEnvironmentContext.get(focusedSymbolKey);

  // No change in selection
  if (nextPath === prevPath) return RENDER.UNDECIDED;
  if (!nextPath.length && !prevPath.length) return RENDER.UNDECIDED;

  // When in a focused component and going from no selection to a selection, we need to re-render
  if (focusedSymbolNativeIdPath?.length && prevPath?.length === 0) {
    return RENDER.REQUIRED;
  }

  // if element is in the next path or the previous path we need to re-render
  const wasSelected =
    prevPath?.length && elementContainsNestedSelection(prevPath)(element);
  const isSelected =
    nextPath?.length && elementContainsNestedSelection(nextPath)(element);

  // Path references changed but element remains selected
  if (wasSelected && isSelected) return RENDER.UNDECIDED;
  // if we were in the previous path we need to re-render to unselect
  if (wasSelected || isSelected) return RENDER.REQUIRED;

  return RENDER.NOT_REQUIRED;
};
const selectedNodeOptimization = (
  prevEnv: Env,
  nextEnv: Env,
  element: ExpressionElement
) => {
  return sharedSelectNodeOptimization(
    prevEnv.environmentContext,
    nextEnv.environmentContext,
    element
  );
};

const contentEditorOptimization = (
  _prevEnv: Env,
  nextEnv: Env,
  element: ExpressionElement,
  rootElement: null | ExpressionElement
) => {
  const {environmentContext, pluginsContext} = nextEnv;
  // @ts-expect-error - TS2339 - Property 'subtree' does not exist on type 'ContextType'.
  const {subtree} = pluginsContext.get('contentEditor') || {};
  const selectedNativeIdPath = environmentContext.get(selectedNativeIdPathKey);

  // When editing a rich text element,the whole ContentEditorStore is being
  // passed down on each update, causing a component update on each
  // keystroke for components that do not need to be re-rendered. This is a
  // temporary workaround to avoid this overkill and if there is a subtree
  // (which only exists when a rich text element is being edited), then we
  // only re-render elements that are related to that existing subtree
  if (subtree && rootElement) {
    const treeIsInElement = isOrContainsElement(rootElement, subtree.id);
    // If the tree we are editing is inside a component we want
    // to proceed with rendering the element
    const designerHasComponentInstanceFocused =
      environmentContext.get(focusedSymbolKey)?.length;
    if (treeIsInElement && designerHasComponentInstanceFocused) {
      return RENDER.REQUIRED;
    }

    // selectedNativeIdPath is undefined inside RichTextInputBody
    if (!selectedNativeIdPath) return;

    if (!treeIsInElement && selectedNativeIdPath.length === 0) {
      return RENDER.NOT_REQUIRED;
    }

    // When an element within a component is selected the currently-rendering
    // node should be re-rendered if it contains any of the element ids
    // within the selection path.
    if (selectedNativeIdPath.length > 0) {
      // Note the `element` here is a component instance
      const isComponentPathContainedInElementBeingRendered =
        elementContainsNestedSelection(selectedNativeIdPath)(element);

      if (!isComponentPathContainedInElementBeingRendered) {
        return RENDER.NOT_REQUIRED;
      }
    }
  }
};

const instanceIndexSuffixDeopt = (
  prev: {environmentContext: EnvironmentContextType},
  next: {environmentContext: EnvironmentContextType}
) => {
  if (
    prev.environmentContext.get('instanceIndexSuffix') !==
    next.environmentContext.get('instanceIndexSuffix')
  ) {
    return RENDER.REQUIRED;
  }
};

// Function to determine if a node in the component tree contains instances of the focused component type
// This ensures that changes to currently edited components are updated everywhere.
const sharedDoesNodeContainFocusedSymbolInstancesOptimization = (
  environmentContext: EnvironmentContextType,
  components: Component.ComponentMap,
  element: ExpressionElement,
  rootElement: null | ExpressionElement
) => {
  const focusedComponentType = environmentContext.get(focusedComponentTypeKey);

  if (!focusedComponentType) return;

  if (rootElement) {
    // Check if the current component depends on the focused component type
    const componentDeps = createComponentMapDependencies(components);
    const currentComponentDeps = componentDeps.get(element.type);
    if (
      currentComponentDeps &&
      currentComponentDeps.some((dep) => namesEqual(focusedComponentType, dep))
    ) {
      return RENDER.REQUIRED;
    }
  }

  if (
    !namesEqual(element.type, focusedComponentType) &&
    isUserComponent(focusedComponentType)
  ) {
    // Find all component instances in the element data
    const inElementDeps = getDirectUserComponentDependencies(element.data);
    if (inElementDeps.some((dep) => namesEqual(focusedComponentType, dep))) {
      return RENDER.REQUIRED;
    }
  }
};
const doesNodeContainFocusedSymbolInstancesOptimization = (
  _prevEnv: Env,
  nextEnv: Env,
  element: ExpressionElement,
  rootElement: null | ExpressionElement
) => {
  return sharedDoesNodeContainFocusedSymbolInstancesOptimization(
    nextEnv.environmentContext,
    nextEnv.components,
    element,
    rootElement
  );
};

/**
 * If the element or the environment changes, we need to re-render the
 * element because the `node` is derived from these two pieces of data.
 */
const elementChangedDeopt = (
  prevProps: ExpansionProps,
  nextProps: ExpansionProps
) => {
  if (prevProps.element !== nextProps.element) {
    return RENDER.REQUIRED;
  }

  if (prevProps.env !== nextProps.env) {
    return RENDER.REQUIRED;
  }
};

const dynPath = ['dyn'];
const commercePath = ['commerce'];

const dynamoChangedDeopt = (
  prevProps: ExpansionProps,
  nextProps: ExpansionProps
) => {
  if (getInPojo(nextProps.data, dynPath)) {
    if (
      prevProps.pluginsContext.get('Dynamo') !==
      nextProps.pluginsContext.get('Dynamo')
    ) {
      return RENDER.REQUIRED;
    }
  }
};

const commerceChangedDeopt = (
  prevProps: ExpansionProps,
  nextProps: ExpansionProps
) => {
  if (
    getPluginName(nextProps.element.type) === 'Commerce' ||
    getInPojo(nextProps.data, commercePath)
  ) {
    if (
      prevProps.pluginsContext.get('Commerce') !==
      nextProps.pluginsContext.get('Commerce')
    ) {
      return RENDER.REQUIRED;
    }
  }
};

const getPaginationPageNumbers = getIn(['Pagination', 'pageNumbers']);
const getPaginationTotalPages = getIn(['Pagination', 'totalPages']);
const paginationChangedOptimization = (
  prevProps: ExpansionProps,
  nextProps: ExpansionProps
) => {
  const {element} = nextProps;

  if (
    isPagination(element) ||
    isPaginationNext(element) ||
    isPaginationPrevious(element) ||
    isPaginationCount(element)
  ) {
    const prevPageNumbers = getPaginationPageNumbers(prevProps.pluginsContext);
    const prevTotalPages = getPaginationTotalPages(prevProps.pluginsContext);

    const nextPageNumbers = getPaginationPageNumbers(nextProps.pluginsContext);
    const nextTotalPages = getPaginationTotalPages(nextProps.pluginsContext);

    if (
      prevPageNumbers !== nextPageNumbers ||
      prevTotalPages !== nextTotalPages
    ) {
      return RENDER.REQUIRED;
    }
  }
};

// We want to make sure nodes that were affected are re-rendered to remove the class
const elementAffectedNodeChangedDeopt = (
  prevProps: ExpansionProps,
  nextProps: ExpansionProps
) => {
  return sharedAffectedNodeChangedDeopt(
    prevProps.environmentContext,
    nextProps.environmentContext,
    nextProps.element
  );
};

const elementSelectedNodeOptimization = (
  prevProps: ExpansionProps,
  nextProps: ExpansionProps
) => {
  return sharedSelectNodeOptimization(
    prevProps.environmentContext,
    nextProps.environmentContext,
    nextProps.element
  );
};

const elementDoesNodeContainFocusedSymbolInstancesOptimization = (
  _prevProps: ExpansionProps,
  nextProps: ExpansionProps
) => {
  return sharedDoesNodeContainFocusedSymbolInstancesOptimization(
    nextProps.environmentContext,
    nextProps.components,
    nextProps.element,
    null
  );
};

const elementContentEditorOptimization = (
  _prevProps: ExpansionProps,
  nextProps: ExpansionProps
) => {
  const {environmentContext, pluginsContext, element} = nextProps;
  const subtreeId = getContentEditorSubtreeId(pluginsContext);
  const selectedNativeIdPath = environmentContext.get(selectedNativeIdPathKey);

  // When editing a rich text element,the whole ContentEditorStore is being
  // passed down on each update, causing a component update on each
  // keystroke for components that do not need to be re-rendered. This is a
  // temporary workaround to avoid this overkill and if there is a subtree
  // (which only exists when a rich text element is being edited), then we
  // only re-render elements that are related to that existing subtree
  if (subtreeId) {
    const treeIsInElement = isOrContainsElement(element, subtreeId);
    // If the tree we are editing is inside a component we want
    // to proceed with rendering the element
    const isElementInComponentInstance = nextProps.nativeIdPath.length > 0;
    const designerHasComponentInstanceFocused =
      nextProps.environmentContext.get(focusedSymbolKey)?.length;
    if (
      treeIsInElement &&
      isElementInComponentInstance &&
      designerHasComponentInstanceFocused
    ) {
      return RENDER.REQUIRED;
    }

    // selectedNativeIdPath is undefined inside RichTextInputBody
    if (!selectedNativeIdPath) return;

    if (!treeIsInElement && selectedNativeIdPath.length === 0) {
      return RENDER.NOT_REQUIRED;
    }

    // When a node within a symbol is selected (and therefore
    // `selectedNativeIdPath` begins with at least one symbol
    // instance nativeId), the currently-rendering node should be re-rendered if
    // it contains any of the symbol instances within the selection path.
    if (selectedNativeIdPath.length > 0) {
      const isSymbolPathContainedInElementBeingRendered =
        elementContainsNestedSelection(selectedNativeIdPath)(element);

      if (!isSymbolPathContainedInElementBeingRendered) {
        return RENDER.NOT_REQUIRED;
      }
    }
  }
};

// Decides what `commonEqualProps` should do for an atom while the content
// editor is actively editing a subtree. The contentEditor broadcasts
// `pluginsContext` on every keystroke, so atoms whose rendering isn't
// affected can short-circuit.
const contentEditorAtomMemoInstruction = (
  prev: EvalElementProps,
  next: EvalElementProps,
  nextSubtreeId: string
): RENDER_INSTRUCTION => {
  if (
    prev.env.environmentContext === next.env.environmentContext &&
    prev.env.pluginsContext === next.env.pluginsContext
  ) {
    return RENDER.UNDECIDED;
  }

  const {environmentContext: prevEC, pluginsContext: prevPC} = prev.env;
  const {environmentContext: nextEC} = next.env;
  const prevSubtreeId = getContentEditorSubtreeId(prevPC);
  const prevSel = prevEC.get(selectedNativeIdPathKey);
  const nextSel = nextEC.get(selectedNativeIdPathKey);
  const prevAff = prevEC.get(affectedNodeIdsKey);
  const nextAff = nextEC.get(affectedNodeIdsKey);
  const prevSym = prevEC.get(focusedSymbolKey);
  const nextSym = nextEC.get(focusedSymbolKey);

  const didContentEditorContextChange =
    prevSubtreeId !== nextSubtreeId ||
    prevSel !== nextSel ||
    prevAff !== nextAff ||
    prevSym !== nextSym;

  if (!didContentEditorContextChange) {
    return RENDER.UNDECIDED;
  }

  const elementId = next.element.id;
  if (
    (prevAff && prevAff.includes(elementId)) ||
    (nextAff && nextAff.includes(elementId))
  ) {
    return RENDER.UNDECIDED;
  }

  const treeIsInElement = isOrContainsElement(next.element, nextSubtreeId);
  const isElementInComponentInstance = next.env.nativeIdPath.length > 0;
  if (treeIsInElement && isElementInComponentInstance && nextSym?.length) {
    return RENDER.REQUIRED;
  }

  // selectedNativeIdPath is undefined inside RichTextInputBody. When a
  // symbol is focused and its subtree contains this element, render so
  // edit affordances (e.g. mini-toolbar) stay current.
  if (!nextSel) {
    if (nextSym?.length && treeIsInElement) {
      return RENDER.REQUIRED;
    }
    return RENDER.UNDECIDED;
  }

  // When this atom contains the active subtree and the selection path
  // changed, re-render so selection-aware UI stays in sync.
  if (treeIsInElement && prevSel !== nextSel) {
    return RENDER.REQUIRED;
  }

  // When a symbol is focused, atoms outside the active subtree may
  // still need to re-render on selection change so edit affordances
  // (e.g. mini-toolbar) stay in sync.
  if (nextSym?.length && prevSel !== nextSel) {
    return RENDER.REQUIRED;
  }

  const shouldSkip =
    (!treeIsInElement && nextSel.length === 0) ||
    (nextSel.length > 0 &&
      !elementContainsNestedSelection(nextSel)(next.element));

  if (!shouldSkip) return RENDER.UNDECIDED;

  // A focused user component can reach into this atom through paths
  // the atom-level check can't see (e.g. slots, prop bindings,
  // transitive deps). Defer to commonEqualProps.
  const focusedComponentType = nextEC.get(focusedComponentTypeKey);
  if (focusedComponentType && isUserComponent(focusedComponentType)) {
    return RENDER.UNDECIDED;
  }
  return RENDER.NOT_REQUIRED;
};

/**
 * Render if:
 *   - Any function returns `REQUIRED`
 *   - Every function returns `UNDECIDED`
 *
 * Don't Render if:
 *   -  Any function returns `NOT_REQUIRED` and no other functions return `REQUIRED`
 */
export function evalElementMemo(
  prev: ExpansionProps,
  next: ExpansionProps
): boolean {
  const optimizations: ElementOptimizer[] = [
    sharedContextDeopt,
    elementChangedDeopt,
    dynamoChangedDeopt,
    commerceChangedDeopt,
    layoutChangedDeopt,
    tabsChangedDeopt,
    elementAffectedNodeChangedDeopt,
    elementSelectedNodeOptimization,
    elementContentEditorOptimization,
    paginationChangedOptimization,
    instanceIndexSuffixDeopt,
    elementDoesNodeContainFocusedSymbolInstancesOptimization,
  ];

  let isEqual = false;

  for (const optimization of optimizations) {
    const instruction = optimization(prev, next);
    if (instruction === RENDER.REQUIRED) {
      return false;
    } else if (instruction === RENDER.NOT_REQUIRED) {
      isEqual = true;
    }
  }
  return isEqual;
}

// Behaves the same evalElementMemo with its return value
function evalComponentMemo(
  p: Env,
  n: Env,
  e: ExpressionElement,
  rootElement: null | ExpressionElement
) {
  const optimizations: ComponentInstanceOptimizer[] = [
    sharedContextDeopt,
    evalEnvChangedDeopt,
    layoutChangedDeopt,
    tabsChangedDeopt,
    affectedNodeChangedDeopt,
    selectedNodeOptimization,
    contentEditorOptimization,
    instanceIndexSuffixDeopt,
    doesNodeContainFocusedSymbolInstancesOptimization,
  ];

  let isEqual = false;

  for (const optimization of optimizations) {
    const instruction = optimization(p, n, e, rootElement);
    if (instruction === RENDER.REQUIRED) {
      return false;
    } else if (instruction === RENDER.NOT_REQUIRED) {
      isEqual = true;
    }
  }
  return isEqual;
}

// Component dependency check related utils below

const userComponentDependencyChanged = (
  componentDeps: ComponentDependencyMap,
  element: ExpressionElement,
  nextComponents: Component.ComponentMap,
  prevComponents: Component.ComponentMap
) => {
  if (isUserComponent(element.type)) {
    // Check all deps if they have changed on the root component
    if (
      hasChangedDependency(
        componentDeps,
        element.type,
        nextComponents,
        prevComponents,
        new Set()
      )
    ) {
      return true;
    }
  }

  // Find all component instances in the element data
  const inElementDeps = getDirectUserComponentDependencies(element.data);
  for (const dep of inElementDeps) {
    if (
      hasChangedDependency(
        componentDeps,
        dep,
        nextComponents,
        prevComponents,
        new Set()
      )
    ) {
      return true;
    }
  }
};

const hasChangedDependency = (
  componentDeps: ComponentDependencyMap,
  type: Plugins.QualifiedElement,
  nextComponents: Component.ComponentMap,
  prevComponents: Component.ComponentMap,
  seenComponents: Set<string>
): boolean => {
  const nextComponent = nextComponents.get(type);
  const prevComponent = prevComponents.get(type);
  if (nextComponent !== prevComponent) {
    return true;
  }

  // Make sure to track what components we have checked.
  // (uses only the name of the type for the handled elements
  // to avoid creating another qualified map)
  seenComponents.add(getName(type));

  // Check out the nested deps
  const nestedDeps = componentDeps.get(type);
  if (nestedDeps) {
    for (const dep of nestedDeps) {
      if (
        !seenComponents.has(getName(dep)) &&
        hasChangedDependency(
          componentDeps,
          dep,
          nextComponents,
          prevComponents,
          seenComponents
        )
      ) {
        return true;
      }
    }
  }

  return false;
};

const getComponentDependencies = weakMemo(
  (component: Component.ComponentType): Array<Plugins.QualifiedElement> => {
    const render = Component.getRender(component);
    if (!render) return emptyArray;
    return [
      ...getDirectUserComponentDependencies(render),
      ...getSlotDefaultComponentTypes(component),
    ];
  }
);

type ComponentDependencyMap = MutableQualifiedMap<
  string,
  Array<Plugins.QualifiedElement>
>;
const createComponentMapDependencies = weakMemo(
  (components: Component.ComponentMap): ComponentDependencyMap => {
    const componentMapDeps: ComponentDependencyMap = new MutableQualifiedMap();
    components.forEach((component, key) => {
      if (isUserComponent(key)) {
        const componentDeps = getComponentDependencies(component);
        componentMapDeps.set_mut(key, componentDeps);
      }
    });
    return componentMapDeps;
  }
);

// This is the main propsEqual function that is used to determine if a component
// should re-render or not.
export const componentEqualProps = memoize(
  (runtimeEnvironment: Environment) =>
    (prev: EvalElementProps, next: EvalElementProps): boolean => {
      // These component specific checks are only relevant in the Designer environment
      if (runtimeEnvironment !== WebflowEnvironments.DESIGNER) {
        return commonEqualProps(prev, next);
      }

      const prevEnv = prev.env;
      const nextEnv = next.env;

      // Element instance is unchanged
      if (prev.element !== next.element) return false;

      // Component definitions are unchanged (since prev and next
      // element are unchanged we can use either to get the instance)
      //
      // If some component within the given element is changed
      // return false in order to re-render the changed component
      const {element: instanceElement} = next;
      const {components: prevComponents} = prevEnv;
      const {components: nextComponents} = nextEnv;
      const componentDeps = createComponentMapDependencies(nextComponents);
      if (
        userComponentDependencyChanged(
          componentDeps,
          instanceElement,
          nextComponents,
          prevComponents
        )
      ) {
        return false;
      }

      if (
        prevEnv.topEvalEnv === nextEnv.topEvalEnv &&
        prevEnv.evalEnv === nextEnv.evalEnv &&
        prevEnv.componentContext === nextEnv.componentContext &&
        prevEnv.plugins === nextEnv.plugins &&
        prevEnv.sharedContext === nextEnv.sharedContext &&
        prevEnv.environmentContext === nextEnv.environmentContext &&
        prevEnv.pluginsContext === nextEnv.pluginsContext
      ) {
        return true;
      }

      // Component should update checks using the root and instance element.
      // (next and prev component are equal at this point so we can use either)
      const nextComponent = nextComponents.get(instanceElement.type);
      if (nextComponent) {
        const renderExpr = Component.getRender(nextComponent);
        const rootElement = Expressions.getElement(renderExpr);
        if (rootElement) {
          return evalComponentMemo(
            prev.env,
            next.env,
            instanceElement,
            rootElement
          );
        }
      }

      return false;
    }
);

// This is the common propsEqual function that is used to determine if an atom
// should re-render or not. Note that Atoms also use the shouldComponentUpdate
// from the provided DesignerExpansion in addition to this function.
export const commonEqualProps = (
  prev: EvalElementProps,
  next: EvalElementProps
) => {
  if (prev.element !== next.element) return false;

  const prevEnv = prev.env;
  const nextEnv = next.env;

  // If some component within the given element is changed
  // return false in order to re-render the changed component
  const componentDeps = createComponentMapDependencies(nextEnv.components);
  if (
    userComponentDependencyChanged(
      componentDeps,
      next.element,
      nextEnv.components,
      prevEnv.components
    )
  ) {
    return false;
  }

  if (
    prevEnv.topEvalEnv === nextEnv.topEvalEnv &&
    prevEnv.evalEnv === nextEnv.evalEnv &&
    prevEnv.componentContext === nextEnv.componentContext &&
    prevEnv.plugins === nextEnv.plugins &&
    prevEnv.sharedContext === nextEnv.sharedContext &&
    prevEnv.environmentContext === nextEnv.environmentContext &&
    prevEnv.pluginsContext === nextEnv.pluginsContext
  ) {
    return true;
  }

  // Atoms outside of component instances can short-circuit when the content
  // editor is actively editing a subtree. Component-instance atoms are
  // sensitive to selection-driven edit affordances (for example, slot
  // mini-toolbar activation), so they keep the regular re-render behavior.
  const subtreeId = getContentEditorSubtreeId(nextEnv.pluginsContext);
  if (subtreeId && nextEnv.nativeIdPath.length === 0) {
    if (
      contentEditorAtomMemoInstruction(prev, next, subtreeId) ===
      RENDER.NOT_REQUIRED
    ) {
      return true;
    }
  }

  return false;
};
